import { type RefObject, useCallback, useEffect, useRef } from 'react';

/**
 * React Hook that observes the size of a DOM element and triggers a callback when it changes.
 * @param ref - Reference to the element to be observed.
 * @param callback - Callback function to be invoked when the element is resized.
 */
export function useResizeObserver(
  ref: RefObject<HTMLElement>,
  callback: () => void,
) {
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const observeResize = useCallback(() => {
    if (!resizeObserverRef.current) {
      resizeObserverRef.current = new ResizeObserver(callbackRef.current);
    }

    if (ref.current instanceof HTMLElement) {
      resizeObserverRef.current.observe(ref.current);
    }
  }, [ref]);

  useEffect(() => {
    observeResize();

    return () => {
      resizeObserverRef.current?.disconnect();
    };
  }, [ref, observeResize]);
}
